import { useEffect, useState } from "react";
import { RotateCw, X } from "lucide-react";

const STORAGE_KEY = "quran_rotate_hint_dismissed";

/**
 * Subtle "rotate for double-page" hint shown only when:
 *   • viewport ≥ 820px (would-be tablet that *could* show 2 pages)
 *   • current orientation is portrait
 *   • user hasn't dismissed it before
 *
 * Auto-fades after 8s; manually dismissable via the X.
 * Re-appears on next session if window changes back into the eligible zone.
 */
export default function RotateHint() {
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(STORAGE_KEY) === "1") return;

    // Show only when viewport is wide enough to *unlock* double-page (≥800px)
    // AND it's currently in portrait (taller than wide). Direct dimension
    // checks are more reliable than matchMedia on Samsung/Android tablets.
    const evaluate = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setVisible(w >= 800 && h > w);
    };
    evaluate();
    window.addEventListener("resize", evaluate);
    window.addEventListener("orientationchange", evaluate);
    const id = setInterval(evaluate, 1500);
    return () => {
      window.removeEventListener("resize", evaluate);
      window.removeEventListener("orientationchange", evaluate);
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    if (!visible) return;
    const fadeT = setTimeout(() => setFading(true), 7000);
    const hideT = setTimeout(() => setVisible(false), 8000);
    return () => {
      clearTimeout(fadeT);
      clearTimeout(hideT);
    };
  }, [visible]);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
  };

  return (
    <div
      data-testid="rotate-hint"
      className={`fixed left-1/2 -translate-x-1/2 bottom-24 z-40 transition-opacity duration-500 ${
        fading ? "opacity-0" : "opacity-100"
      }`}
    >
      <div className="flex items-center gap-3 bg-islamic-blue-900/95 backdrop-blur border border-gold-400/30 rounded-full pl-4 pr-2 py-2 shadow-xl">
        <RotateCw className="w-4 h-4 text-gold-400 shrink-0" />
        <p className="text-slate-200 text-sm font-outfit">
          Rotate to landscape for the two-page Mushaf
        </p>
        <button
          data-testid="rotate-hint-dismiss"
          onClick={dismiss}
          className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-slate-200 ml-1"
          aria-label="Dismiss hint"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
